import React, { useEffect, useRef, useState } from 'react'
import { supabase } from './supabase'

// ============================================================================
// HomePros — IdxListings (ARMLS IDX listings, read with the IDX key)
// ============================================================================
// Renders listings from the get-idx-listings edge function on the public
// /listings page, agent pages (/<slug>, /links/<slug>) and funnels
// (/funnel/<slug>). The edge function checks the page and the browser origin
// and returns { listings: [], hot: [] } when either is not allowed, so this
// component shows nothing (or emptyFallback) in that case.
//
// HomePros pages: HomePros office listings only (Section 1).
// HomePros Advantage pages and /listings (broad: true): Section 1 is the
// HomePros and HomePros Advantage listings, and Section 2 is the hot sheet,
// every brokerage's listings new in the last 72 hours. Only the first batch
// of hot-sheet cards arrives with photos (the first 7 each); "See more" loads
// the next batch's, and the full set loads when the visitor unlocks.
//
// Props:
//   page          'listings' | 'agent:<slug>' | 'funnel:<slug>', sent to the
//                 edge function, which decides whether the page may show IDX.
//   source        routing tag stamped on the listing_leads insert. Defaults to
//                 the broker tag, like FeaturedListings.
//   lang          'en' | 'es'. All UI text follows it except the ARMLS line,
//                 which stays in English.
//   variant       'page' shows every listing. 'agent' and 'funnel' show the
//                 featured card and three more, then a button that expands
//                 the rest in place, so leads stay with this page's source.
//   agentFirst    first name used in the unlock form's subtitle.
//   emptyFallback shown when the fetch finishes with no listings or fails.
//   frame         optional (content) => ReactNode wrapper, rendered only when
//                 there are listings.
//
// Never shows a listing agent's name or phone: the edge function does not
// send them. ARMLS 23.2.12 and 23.2.13: cards (minimal displays that open the
// detail sheet) show "Listed by <office>" only; the detail sheet shows it
// with the listing office email (or phone, office_contact) under it, in the
// same text size as the listing details.
// ============================================================================

const DEFAULT_SOURCE = 'HomePros Hub Listings'

const IDX_URL =
  'https://oeeanuemukqkwhtlkqij.supabase.co/functions/v1/get-idx-listings'

const COLLAPSED_COUNT = 3 // pop-out cards shown before "See all" on agent and funnel pages
const FREE_PHOTOS = 6
// Hot sheet: cards per load (featured card included). Agent and funnel pages
// start at 1 featured + 3 pop cards and add 4; /listings shows and adds 12.
const HOT_STEP_COMPACT = 4
const HOT_STEP_PAGE = 12

// ARMLS attribution. Stays in English in both languages. Pages with the hot
// sheet also show other brokerages' listings.
const ARMLS_SOURCE = 'Listing information source: ARMLS. All information should be verified by the recipient and none is guaranteed as accurate by ARMLS.'
const armlsLine = (broad) => `${ARMLS_SOURCE} ${broad ? 'Listings shown include listings from other brokerages.' : 'Listings shown are HomePros listings.'}`

const T = {
  en: {
    eyebrow: 'HomePros listings',
    eyebrowBroad: 'HomePros & HomePros Advantage listings',
    heading: 'Homes for sale',
    hotHeading: 'New in the last 72 hours',
    newCount: (n) => `${n} new`,
    seeMore: 'See more',
    active: (n) => `${n} active`,
    forSale: 'For sale',
    viewHome: 'View home →',
    seeAllListings: 'See all HomePros listings',
    seeAllOur: 'See all our listings',
    listedBy: (o) => `Listed by ${o}`,
    photos: (n) => `${n} photos`,
    seeAllPhotos: (n) => `See all ${n} photos`,
    unlockTitle: 'Unlock all photos',
    showingTitle: 'Request a private showing',
    fullName: 'Full name',
    phone: 'Phone',
    emailOptional: 'Email (optional)',
    submit: 'Unlock photos & request showing',
    submitting: 'Submitting…',
    reachOut: (a) => (a ? `${a} will reach out about a private showing.` : "We'll reach out about a private showing."),
    thanks: 'Thanks! Every photo is unlocked.',
    submitFailed: 'Something went wrong. Please try again.',
    bd: 'bd', ba: 'ba', sqft: 'sqft',
    beds: 'Beds', baths: 'Baths', area: 'Sq ft',
    close: 'Close',
    viewPhoto: (i) => `View photo ${i}`,
    details: 'Listing details',
    errName: 'Please enter your name.',
    errPhone: 'Please enter a valid 10-digit phone number.',
    errEmail: 'Please enter a valid email address.',
    errEmailDisposable: 'Please use a permanent email address.',
    dpr: 'May qualify for down payment help',
    dprCount: (n, total) => `${n} of ${total} homes may qualify for down payment help`,
    dprNote: 'Eligibility depends on the buyer and the program.',
    dprAsk: 'Ask about down payment programs',
  },
  es: {
    eyebrow: 'Listados de HomePros',
    eyebrowBroad: 'Listados de HomePros y HomePros Advantage',
    heading: 'Casas en venta',
    hotHeading: 'Nuevas en las últimas 72 horas',
    newCount: (n) => `${n} nuevas`,
    seeMore: 'Ver más',
    active: (n) => `${n} activas`,
    forSale: 'En venta',
    viewHome: 'Ver casa →',
    seeAllListings: 'Ver todos los listados de HomePros',
    seeAllOur: 'Ver todos nuestros listados',
    listedBy: (o) => `Listado por ${o}`,
    photos: (n) => `${n} fotos`,
    seeAllPhotos: (n) => `Ver las ${n} fotos`,
    unlockTitle: 'Desbloquea todas las fotos',
    showingTitle: 'Pide una visita privada',
    fullName: 'Nombre completo',
    phone: 'Teléfono',
    emailOptional: 'Correo (opcional)',
    submit: 'Desbloquear fotos y pedir una visita',
    submitting: 'Enviando…',
    reachOut: (a) => (a ? `${a} te contactará para una visita privada.` : 'Te contactaremos para una visita privada.'),
    thanks: '¡Gracias! Ya puedes ver todas las fotos.',
    submitFailed: 'Algo salió mal. Por favor inténtalo de nuevo.',
    bd: 'rec.', ba: 'baños', sqft: 'pies²',
    beds: 'Recámaras', baths: 'Baños', area: 'Pies²',
    close: 'Cerrar',
    viewPhoto: (i) => `Ver foto ${i}`,
    details: 'Detalles de la casa',
    errName: 'Por favor escribe tu nombre.',
    errPhone: 'Escribe un teléfono válido de 10 dígitos.',
    errEmail: 'Escribe un correo válido.',
    errEmailDisposable: 'Usa un correo permanente.',
    dpr: 'Puede calificar para ayuda de enganche',
    dprCount: (n, total) => `${n} de ${total} casas pueden calificar para ayuda de enganche`,
    dprNote: 'La elegibilidad depende del comprador y del programa.',
    dprAsk: 'Pregunta por los programas de enganche',
  },
}

function formatPrice(n) {
  const num = Number(n)
  if (!isFinite(num) || num <= 0) return ''
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(num)
}

function formatSqft(n) {
  const num = Number(n)
  if (!isFinite(num) || num <= 0) return ''
  return new Intl.NumberFormat('en-US').format(num)
}

// "City, AZ ZIP", leaving out whatever is missing.
function cityLine(l) {
  const city = l?.city || ''
  const zip = l?.zip || ''
  return [city, ['AZ', zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')
}

// Street address, or the city alone when the seller hid the address.
function streetOrCity(l) {
  return l?.address || l?.city || ''
}

function metaLine(l, t) {
  const parts = []
  if (l?.beds) parts.push(`${l.beds} ${t.bd}`)
  if (l?.baths) parts.push(`${l.baths} ${t.ba}`)
  const sqft = formatSqft(l?.sqft)
  if (sqft) parts.push(`${sqft} ${t.sqft}`)
  return parts.join(' · ')
}

function photoList(l) {
  return Array.isArray(l?.photos) ? l.photos : []
}

// Real photo count. Hot-sheet listings arrive with their first photos only and
// photo_count carrying the total.
function photoTotal(l) {
  const n = Number(l?.photo_count)
  return Number.isFinite(n) && n > 0 ? n : photoList(l).length
}

// ─── LEAD SUBMIT (copied from FeaturedListings) ─────────────────────────────
// Inserts the captured lead into Supabase, then fires the FUB-routing edge
// function fire-and-forget. Insert is the source of truth (throws on failure
// so the form re-enables submit and the buyer can retry); the FUB push is
// best-effort.
async function submitLead(payload) {
  // Client-generated id: RETURNING would require a SELECT policy anon doesn't
  // have.
  const id = crypto.randomUUID()
  const { error } = await supabase
    .from('listing_leads')
    .insert({
      id,
      name: payload.name,
      phone: payload.phone,
      // Email is optional on the form — coerce undefined → null so the DB
      // gets an explicit NULL rather than relying on column defaults.
      email: payload.email || null,
      mls: payload.mls,
      address: payload.address,
      office: payload.office,
      source: payload.source || DEFAULT_SOURCE,
    })
  if (error) {
    console.error('[listing_leads] insert failed:', error)
    throw error
  }

  // Fire-and-forget FUB push. A FUB failure must NOT bubble up — the row is
  // already in listing_leads (the source of truth), retryable later.
  supabase.functions
    .invoke('send-listing-lead-to-fub', { body: { lead_id: id } })
    .then(({ data, error: fnErr }) => {
      if (fnErr) console.error('[fub push] invoke failed:', fnErr)
      else if (data?.error) console.error('[fub push] function returned error:', data.error)
    })
    .catch((e) => console.error('[fub push] threw:', e))
}

// Disposable email blocklist, copied from FeaturedListings.
const DISPOSABLE_EMAIL_DOMAINS = [
  'mailinator.com', 'guerrillamail.com', '10minutemail.com', 'tempmail.com',
  'throwaway.email', 'yopmail.com', 'trashmail.com', 'sharklasers.com',
  'getnada.com', 'temp-mail.org', 'fakeinbox.com', 'maildrop.cc',
]

// Same checks as FeaturedListings' validateLead; only the messages come from
// the language table. Empty string = field is OK.
function validateLead({ name, phone, email }, t) {
  const errors = { name: '', phone: '', email: '' }

  // NAME — trimmed length >= 2 and not just a single repeated character.
  const trimmedName = name.trim()
  const nameChars = new Set(trimmedName.toLowerCase())
  if (trimmedName.length < 2 || nameChars.size === 1) {
    errors.name = t.errName
  }

  // PHONE — strip to digits, must be exactly 10, reject obvious junk.
  const digits = phone.replace(/\D/g, '')
  const allSame = /^(\d)\1{9}$/.test(digits)
  const sequential = digits === '1234567890' || digits === '0987654321'
  if (digits.length !== 10 || allSame || sequential) {
    errors.phone = t.errPhone
  }

  // EMAIL — optional. Skip entirely when blank; otherwise check shape + domain.
  const trimmedEmail = email.trim()
  if (trimmedEmail) {
    const shapeOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)
    if (!shapeOk) {
      errors.email = t.errEmail
    } else {
      const domain = trimmedEmail.split('@')[1].toLowerCase()
      if (DISPOSABLE_EMAIL_DOMAINS.includes(domain)) {
        errors.email = t.errEmailDisposable
      }
    }
  }

  return errors
}

// ARMLS "DPR Eligible" (down payment resource) flag, sent by get-idx-listings.
function isDpr(l) {
  return l?.dpr === true
}

// Small stroke check-circle used with the DPR line, banners and box.
function DprIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="idx-dpr-icon">
      <circle cx="12" cy="12" r="9.5" />
      <path d="M8 12.5l2.7 2.7L16 9.5" />
    </svg>
  )
}

// ─── CARDS ──────────────────────────────────────────────────────────────────
function FeaturedCard({ listing, t, onOpen }) {
  const photos = photoList(listing)
  const price = formatPrice(listing?.price)
  const meta = metaLine(listing, t)
  return (
    <button type="button" className="idx-featured" onClick={() => onOpen(listing)}>
      <span className="idx-featured-media">
        {photos[0]
          ? <img className="idx-img" src={photos[0]} alt="" loading="lazy" />
          : <span className="idx-img idx-img-empty" />}
        <span className="idx-pill idx-pill-sale">{t.forSale}</span>
        {photos.length > 0 && <span className="idx-pill idx-pill-count">{t.photos(photoTotal(listing))}</span>}
        {isDpr(listing) && <span className="idx-dpr-banner idx-dpr-banner-featured"><DprIcon />{t.dpr}</span>}
      </span>
      <span className="idx-featured-body">
        {price && <span className="idx-price idx-price-lg">{price}</span>}
        <span className="idx-street">{streetOrCity(listing)}</span>
        <span className="idx-city">{cityLine(listing)}</span>
        {meta && <span className="idx-meta">{meta}</span>}
        <span className="idx-featured-foot">
          <span className="idx-office">{t.listedBy(listing?.office || 'HomePros')}</span>
          <span className="idx-view">{t.viewHome}</span>
        </span>
      </span>
    </button>
  )
}

function PopCard({ listing, t, onOpen }) {
  const photos = photoList(listing)
  const price = formatPrice(listing?.price)
  const meta = metaLine(listing, t)
  return (
    <button type="button" className={`idx-pop${isDpr(listing) ? ' has-dpr' : ''}`} onClick={() => onOpen(listing)}>
      <span className="idx-pop-photo">
        {photos[0]
          ? <img className="idx-img" src={photos[0]} alt="" loading="lazy" />
          : <span className="idx-img idx-img-empty" />}
      </span>
      <span className="idx-pop-body">
        {price && <span className="idx-price">{price}</span>}
        <span className="idx-street">{streetOrCity(listing)}</span>
        <span className="idx-city">{cityLine(listing)}</span>
        {meta && <span className="idx-meta">{meta}</span>}
        <span className="idx-office">{t.listedBy(listing?.office || 'HomePros')}</span>
      </span>
      {isDpr(listing) && <span className="idx-dpr-banner idx-dpr-banner-pop"><DprIcon size={14} />{t.dpr}</span>}
    </button>
  )
}

// ─── DETAIL SHEET ───────────────────────────────────────────────────────────
// Bottom sheet on narrow screens, centered dialog at 720px and up. The first
// FREE_PHOTOS photos are free; the rest unlock after a successful lead submit.
// Like FeaturedListings, the unlock lasts while this sheet is open.
function DetailSheet({ listing, t, page, broad, source, agentFirst, onClose }) {
  const [fullPhotos, setFullPhotos] = useState(null)
  const photos = fullPhotos || photoList(listing)
  const total = photoTotal(listing)
  const [unlocked, setUnlocked] = useState(false)
  const [active, setActive] = useState(0)
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState({ name: '', phone: '', email: '' })
  const [submitting, setSubmitting] = useState(false)
  const [submitFailed, setSubmitFailed] = useState(false)
  const closeRef = useRef(null)
  const formRef = useRef(null)

  // Body scroll lock — restore previous overflow on unmount.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prevOverflow }
  }, [])

  // Escape closes; focus starts on the close button. onClose is read through a
  // ref so a new function from the parent doesn't re-run this and steal focus
  // from the form.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current() }
    document.addEventListener('keydown', onKey)
    if (closeRef.current) closeRef.current.focus()
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // A hot-sheet listing came with its first photos only: after the unlock,
  // load the rest (same page check on the server). If that fails, the photos
  // already here stay.
  useEffect(() => {
    if (!unlocked || !page || !listing?.mls || total <= photoList(listing).length) return undefined
    let cancelled = false
    fetch(`${IDX_URL}?page=${encodeURIComponent(page)}&mls=${encodeURIComponent(listing.mls)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && Array.isArray(data?.photos) && data.photos.length > 0) setFullPhotos(data.photos)
      })
      .catch((err) => console.error('[get-idx-listings] photos failed:', err))
    return () => { cancelled = true }
  }, [unlocked, page, listing, total])

  const locked = !unlocked && total > FREE_PHOTOS
  const visible = unlocked ? photos : photos.slice(0, FREE_PHOTOS)
  const current = visible[Math.min(active, Math.max(visible.length - 1, 0))] || ''
  const price = formatPrice(listing?.price)
  const sqft = formatSqft(listing?.sqft)

  const clearError = (field) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (submitting || unlocked) return
    const next = validateLead({ name, phone, email }, t)
    if (next.name || next.phone || next.email) {
      setErrors(next)
      return
    }
    setErrors({ name: '', phone: '', email: '' })
    setSubmitFailed(false)
    setSubmitting(true)
    try {
      // Normalize phone to bare digits, as FeaturedListings does.
      const phoneDigits = phone.replace(/\D/g, '')
      const trimmedEmail = email.trim()
      await submitLead({
        name: name.trim(),
        phone: phoneDigits,
        email: trimmedEmail || undefined,
        mls: listing?.mls,
        address: listing?.address,
        office: listing?.office || 'HomePros',
        source,
      })
      setUnlocked(true)
    } catch (err) {
      console.error('[lead submit] failed:', err)
      setSubmitFailed(true)
    } finally {
      setSubmitting(false)
    }
  }

  const goToForm = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const first = formRef.current.querySelector('input')
      if (first) first.focus({ preventScroll: true })
    }
  }

  return (
    <div className="idx-backdrop" onClick={onClose}>
      <div
        className="idx-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={t.details}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="idx-sheet-media">
          {current
            ? <img className="idx-img" src={current} alt="" loading="lazy" />
            : <span className="idx-img idx-img-empty" />}
          <span className="idx-pill idx-pill-sale">{t.forSale}</span>
          <button ref={closeRef} type="button" className="idx-close" onClick={onClose} aria-label={t.close}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        {photos.length > 1 && (
          <div className="idx-thumbs">
            {visible.map((url, i) => (
              <button
                key={url}
                type="button"
                className={`idx-thumb${i === active ? ' is-active' : ''}`}
                onClick={() => setActive(i)}
                aria-label={t.viewPhoto(i + 1)}
                aria-pressed={i === active}
              >
                <img className="idx-img" src={url} alt="" loading="lazy" />
              </button>
            ))}
            {locked && (
              <button type="button" className="idx-thumb idx-thumb-locked" onClick={goToForm}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
                <span>{t.seeAllPhotos(total)}</span>
              </button>
            )}
          </div>
        )}

        <div className="idx-sheet-body">
          {price && <div className="idx-price idx-price-lg">{price}</div>}
          <div className="idx-street idx-street-lg">{streetOrCity(listing)}</div>
          <div className="idx-city">{cityLine(listing)}</div>

          <div className="idx-stats">
            <div className="idx-stat"><span className="idx-stat-val">{listing?.beds || '—'}</span><span className="idx-stat-label">{t.beds}</span></div>
            <div className="idx-stat"><span className="idx-stat-val">{listing?.baths || '—'}</span><span className="idx-stat-label">{t.baths}</span></div>
            <div className="idx-stat"><span className="idx-stat-val">{sqft || '—'}</span><span className="idx-stat-label">{t.area}</span></div>
          </div>
          {isDpr(listing) && (
            <div className="idx-dpr-box">
              <div className="idx-dpr-box-title"><DprIcon size={18} />{t.dpr}</div>
              <div className="idx-dpr-box-note">{t.dprNote}</div>
              {!unlocked && (
                <button type="button" className="idx-dpr-ask" onClick={goToForm}>{t.dprAsk}</button>
              )}
            </div>
          )}
          <div className="idx-sheet-office">
            <div>{t.listedBy(listing?.office || 'HomePros')}</div>
            {listing?.office_contact && <div className="idx-contact">{listing.office_contact}</div>}
          </div>

          {unlocked ? (
            <div className="idx-thanks" role="status">{t.thanks}</div>
          ) : (
            <form ref={formRef} className="idx-form" onSubmit={handleSubmit} noValidate>
              <div className="idx-form-title">{locked ? t.unlockTitle : t.showingTitle}</div>
              <div className="idx-form-sub">{t.reachOut(agentFirst)}</div>
              <label className="idx-field">
                <span>{t.fullName}</span>
                <input value={name} onChange={(e) => { setName(e.target.value); clearError('name') }} autoComplete="name" disabled={submitting} />
                {errors.name && <span className="idx-err">{errors.name}</span>}
              </label>
              <label className="idx-field">
                <span>{t.phone}</span>
                <input type="tel" inputMode="tel" value={phone} onChange={(e) => { setPhone(e.target.value); clearError('phone') }} autoComplete="tel" disabled={submitting} />
                {errors.phone && <span className="idx-err">{errors.phone}</span>}
              </label>
              <label className="idx-field">
                <span>{t.emailOptional}</span>
                <input type="email" inputMode="email" value={email} onChange={(e) => { setEmail(e.target.value); clearError('email') }} autoComplete="email" disabled={submitting} />
                {errors.email && <span className="idx-err">{errors.email}</span>}
              </label>
              <button type="submit" className="idx-submit" disabled={submitting || !name.trim() || !phone.trim()} aria-busy={submitting}>
                {submitting ? t.submitting : t.submit}
              </button>
              {submitFailed && <div className="idx-err" role="alert">{t.submitFailed}</div>}
            </form>
          )}

          <p className="idx-armls">{armlsLine(broad)}</p>
        </div>
      </div>
    </div>
  )
}

// ─── IDX LISTINGS ───────────────────────────────────────────────────────────
export function IdxListings({ page, source = DEFAULT_SOURCE, lang = 'en', variant, agentFirst, emptyFallback = null, frame = null }) {
  const [status, setStatus] = useState('loading') // 'loading' | 'ready' | 'empty' | 'error'
  const [listings, setListings] = useState([])
  const [hot, setHot] = useState([])
  const [broad, setBroad] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const hotStep = variant === 'page' ? HOT_STEP_PAGE : HOT_STEP_COMPACT
  const [hotShown, setHotShown] = useState(hotStep)
  const [loadingMore, setLoadingMore] = useState(false)
  const [selected, setSelected] = useState(null)
  const t = T[lang] || T.en

  useEffect(() => {
    let cancelled = false
    if (!page) { setListings([]); setHot([]); setStatus('empty'); return undefined }
    setStatus('loading')
    fetch(`${IDX_URL}?page=${encodeURIComponent(page)}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data) => {
        if (cancelled) return
        const arr = Array.isArray(data?.listings) ? data.listings : []
        const hotArr = Array.isArray(data?.hot) ? data.hot : []
        setListings(arr)
        setHot(hotArr)
        setBroad(data?.broad === true)
        setStatus(arr.length === 0 && hotArr.length === 0 ? 'empty' : 'ready')
      })
      .catch((err) => {
        if (cancelled) return
        console.error('[get-idx-listings] fetch failed:', err)
        setListings([])
        setHot([])
        setStatus('error')
      })
    return () => { cancelled = true }
  }, [page])

  // Nothing while loading; emptyFallback (or nothing) when there are no
  // listings. No heading or empty box is left behind.
  if (!(status === 'ready' && (listings.length > 0 || hot.length > 0))) return status === 'loading' ? null : emptyFallback

  const [featured, ...rest] = listings
  const collapsible = variant === 'agent' || variant === 'funnel'
  const shownRest = collapsible && !expanded ? rest.slice(0, COLLAPSED_COUNT) : rest
  const hiddenCount = rest.length - shownRest.length
  const dprCount = listings.filter(isDpr).length

  // Hot sheet: the same cards, a featured card and pop cards, hotShown at a time.
  const [hotFeatured, ...hotRest] = hot.slice(0, hotShown)
  const hotDprCount = hot.filter(isDpr).length

  // "See more": fetch the next batch's first photos, then show the cards. If
  // the photos fail, the cards still show, with the empty photo block.
  const showMoreHot = async () => {
    if (loadingMore) return
    const next = hot.slice(hotShown, hotShown + hotStep)
    const ids = next.filter((l) => l.mls && photoList(l).length === 0 && photoTotal(l) > 0).map((l) => l.mls)
    if (ids.length > 0) {
      setLoadingMore(true)
      try {
        const r = await fetch(`${IDX_URL}?page=${encodeURIComponent(page)}&mls=${ids.map(encodeURIComponent).join(',')}&preview=1`)
        const data = r.ok ? await r.json() : null
        const previews = data?.previews || {}
        setHot((prev) => prev.map((l) => (Array.isArray(previews[l.mls]) ? { ...l, photos: previews[l.mls] } : l)))
      } catch (err) {
        console.error('[get-idx-listings] previews failed:', err)
      } finally {
        setLoadingMore(false)
      }
    }
    setHotShown((n) => n + hotStep)
  }

  const content = (
    <section className="idx-root">
      {listings.length > 0 && (
        <>
          <div className="idx-head">
            <div>
              <div className="idx-eyebrow">{broad ? t.eyebrowBroad : t.eyebrow}</div>
              <h2 className="idx-heading">{t.heading}</h2>
              {dprCount > 0 && <div className="idx-dpr-count"><DprIcon />{t.dprCount(dprCount, listings.length)}</div>}
            </div>
            <span className="idx-pill idx-pill-active">{t.active(listings.length)}</span>
          </div>

          <FeaturedCard listing={featured} t={t} onOpen={setSelected} />

          {shownRest.length > 0 && (
            <div className="idx-pop-list">
              {shownRest.map((l) => (
                <PopCard key={l.mls || `${l.address}-${l.price}`} listing={l} t={t} onOpen={setSelected} />
              ))}
            </div>
          )}

          {hiddenCount > 0 && (
            <button type="button" className="idx-see-all" onClick={() => setExpanded(true)}>
              {broad ? t.seeAllOur : t.seeAllListings}
            </button>
          )}
        </>
      )}

      {hot.length > 0 && (
        <div className={listings.length > 0 ? 'idx-hot' : undefined}>
          <div className="idx-head">
            <div>
              <h2 className="idx-heading">{t.hotHeading}</h2>
              {hotDprCount > 0 && <div className="idx-dpr-count"><DprIcon />{t.dprCount(hotDprCount, hot.length)}</div>}
            </div>
            <span className="idx-pill idx-pill-active">{t.newCount(hot.length)}</span>
          </div>

          <FeaturedCard listing={hotFeatured} t={t} onOpen={setSelected} />

          {hotRest.length > 0 && (
            <div className="idx-pop-list">
              {hotRest.map((l) => (
                <PopCard key={l.mls || `${l.address}-${l.price}`} listing={l} t={t} onOpen={setSelected} />
              ))}
            </div>
          )}

          {hot.length > hotShown && (
            <button type="button" className="idx-see-all" onClick={showMoreHot} disabled={loadingMore} aria-busy={loadingMore}>
              {t.seeMore}
            </button>
          )}
        </div>
      )}

      <p className="idx-armls">{armlsLine(broad)}</p>
    </section>
  )

  return (
    <>
      <style>{css}</style>
      {frame ? frame(content) : content}
      {selected && (
        <DetailSheet
          listing={selected}
          t={t}
          page={page}
          broad={broad}
          source={source}
          agentFirst={agentFirst}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  )
}

export default IdxListings

// ─── STYLES ─────────────────────────────────────────────────────────────────
// Every class is idx- prefixed. Fonts are inherited from the embedding page.
const css = `
  .idx-root, .idx-root *, .idx-backdrop, .idx-backdrop * { box-sizing: border-box; }
  .idx-root { font-family: inherit; color: #162b40; }
  .idx-root button, .idx-backdrop button, .idx-backdrop input { font-family: inherit; }

  .idx-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 12px; margin-bottom: 16px; }
  .idx-eyebrow { font-size: 12px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: #617389; }
  .idx-heading { margin: 4px 0 0; font-size: 26px; line-height: 1.15; font-weight: 800; color: #1c3044; }

  .idx-pill {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 5px 11px; border-radius: 999px;
    font-size: 12px; font-weight: 700; line-height: 1.2; white-space: nowrap;
  }
  .idx-pill-active { background: #eef4fb; color: #1c3044; border: 1px solid #dce3eb; flex-shrink: 0; }
  .idx-pill-sale { position: absolute; top: 12px; left: 12px; background: #fdbf00; color: #162b40; }
  .idx-pill-count { position: absolute; top: 12px; right: 12px; background: rgba(22,43,64,0.78); color: #ffffff; }

  .idx-img { display: block; width: 100%; height: 100%; object-fit: cover; }
  .idx-img-empty { background: #1c3044; }

  .idx-price { font-size: 20px; font-weight: 800; color: #1c3044; line-height: 1.2; }
  .idx-price-lg { font-size: 28px; }
  .idx-street { font-size: 15px; font-weight: 600; color: #162b40; line-height: 1.35; }
  .idx-street-lg { font-size: 17px; margin-top: 4px; }
  .idx-city { font-size: 14px; color: #617389; line-height: 1.35; }
  .idx-meta { font-size: 14px; color: #162b40; margin-top: 2px; }
  .idx-office { font-size: 12px; color: #617389; margin-top: 4px; }

  /* Featured card */
  .idx-featured {
    display: block; width: 100%; padding: 0; text-align: left; cursor: pointer;
    background: #ffffff; border: 1px solid #dce3eb; border-radius: 18px; overflow: hidden;
    box-shadow: 0 8px 24px rgba(22,43,64,0.10); color: inherit;
  }
  .idx-featured:focus-visible, .idx-pop:focus-visible { outline: 3px solid #147efb; outline-offset: 2px; }
  .idx-featured-media { position: relative; display: block; aspect-ratio: 16 / 10; background: #1c3044; }
  .idx-featured-body { display: flex; flex-direction: column; gap: 2px; padding: 16px 18px 14px; }
  .idx-featured-foot {
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    margin-top: 10px; padding-top: 10px; border-top: 1px solid #dce3eb;
  }
  .idx-featured-foot .idx-office { margin-top: 0; }
  /* Detail sheet: listing office and its contact, the size of the listing
     details (.idx-city, .idx-meta). */
  .idx-sheet-office { margin-top: 6px; font-size: 14px; line-height: 1.4; color: #617389; }
  .idx-contact { overflow-wrap: anywhere; }

  /* Hot sheet, under Section 1 */
  .idx-hot { margin-top: 40px; }
  .idx-view { font-size: 14px; font-weight: 700; color: #147efb; white-space: nowrap; }

  /* Pop-out cards: the photo breaks out of the card's top-left corner. */
  .idx-pop-list { display: grid; grid-template-columns: 1fr; gap: 22px; margin-top: 30px; padding-left: 10px; }
  .idx-pop {
    position: relative; display: block; width: 100%; min-height: 112px; text-align: left; cursor: pointer;
    background: #ffffff; border: 1px solid #dce3eb; border-radius: 16px;
    padding: 14px 14px 14px 132px; color: inherit;
    box-shadow: 0 4px 14px rgba(22,43,64,0.06);
  }
  .idx-pop-photo {
    position: absolute; top: -12px; left: -10px; width: 128px; height: 108px;
    border-radius: 12px; overflow: hidden; background: #1c3044;
    box-shadow: 0 8px 18px rgba(22,43,64,0.22);
  }
  .idx-pop-body { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
  .idx-pop-body .idx-street, .idx-pop-body .idx-city { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .idx-see-all {
    display: block; width: 100%; min-height: 48px; margin-top: 22px; cursor: pointer;
    background: #ffffff; color: #147efb; border: 1.5px solid #147efb; border-radius: 12px;
    font-size: 15px; font-weight: 700;
  }
  .idx-see-all:hover { background: #eef4fb; }

  .idx-armls { margin: 18px 0 0; font-size: 11px; line-height: 1.5; color: #617389; }

  @media (min-width: 900px) {
    .idx-pop-list { grid-template-columns: repeat(2, minmax(0, 1fr)); column-gap: 28px; }
  }

  /* Detail sheet: bottom sheet below 720px, centered dialog from 720px. */
  .idx-backdrop {
    position: fixed; inset: 0; z-index: 1000;
    background: rgba(22,43,64,0.6);
    display: flex; align-items: flex-end; justify-content: center;
    font-family: inherit; color: #162b40;
  }
  .idx-sheet {
    position: relative; width: 100%; max-height: 92vh; overflow-y: auto;
    background: #ffffff; border-radius: 20px 20px 0 0;
    box-shadow: 0 -12px 40px rgba(0,0,0,0.25);
  }
  @media (min-width: 720px) {
    .idx-backdrop { align-items: center; padding: 24px; }
    .idx-sheet { max-width: 640px; max-height: 90vh; border-radius: 20px; }
  }
  .idx-sheet-media { position: relative; aspect-ratio: 16 / 10; background: #1c3044; }
  .idx-close {
    position: absolute; top: 10px; right: 10px; width: 44px; height: 44px;
    display: inline-flex; align-items: center; justify-content: center;
    border: none; border-radius: 999px; cursor: pointer;
    background: rgba(22,43,64,0.72); color: #ffffff;
  }
  .idx-close:focus-visible { outline: 3px solid #fdbf00; outline-offset: 2px; }

  .idx-thumbs { display: grid; grid-template-columns: repeat(auto-fill, minmax(64px, 1fr)); gap: 6px; padding: 8px; background: #f4f7fa; }
  .idx-thumb {
    position: relative; min-height: 48px; aspect-ratio: 4 / 3; padding: 0; overflow: hidden; cursor: pointer;
    border: 2px solid transparent; border-radius: 8px; background: #dce3eb;
  }
  .idx-thumb.is-active { border-color: #147efb; }
  .idx-thumb:focus-visible { outline: 3px solid #147efb; outline-offset: 1px; }
  .idx-thumb-locked {
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
    background: #1c3044; color: #ffffff; font-size: 11px; font-weight: 700; line-height: 1.2; text-align: center; padding: 4px;
  }

  .idx-sheet-body { padding: 18px 20px 24px; }
  .idx-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin: 14px 0 6px; }
  .idx-stat { display: flex; flex-direction: column; align-items: center; padding: 10px 6px; border: 1px solid #dce3eb; border-radius: 12px; }
  .idx-stat-val { font-size: 18px; font-weight: 800; color: #1c3044; }
  .idx-stat-label { font-size: 12px; color: #617389; }

  .idx-form { display: flex; flex-direction: column; gap: 12px; margin-top: 18px; padding: 16px; border: 1px solid #dce3eb; border-radius: 14px; background: #f8fafc; }
  .idx-form-title { font-size: 17px; font-weight: 800; color: #1c3044; }
  .idx-form-sub { font-size: 14px; color: #617389; margin-top: -8px; }
  .idx-field { display: flex; flex-direction: column; gap: 4px; font-size: 13px; font-weight: 600; color: #162b40; }
  .idx-field input {
    height: 44px; padding: 0 12px; font-size: 16px; color: #162b40;
    border: 1.5px solid #dce3eb; border-radius: 10px; background: #ffffff;
  }
  .idx-field input:focus { outline: none; border-color: #147efb; box-shadow: 0 0 0 3px rgba(20,126,251,0.15); }
  .idx-err { font-size: 12px; font-weight: 600; color: #c92a2a; }
  .idx-submit {
    min-height: 48px; border: none; border-radius: 12px; cursor: pointer;
    background: #fdbf00; color: #102234; font-size: 15px; font-weight: 800;
  }
  .idx-submit:disabled { opacity: 0.55; cursor: not-allowed; }
  /* ARMLS "DPR Eligible" flag */
  .idx-dpr-icon { flex-shrink: 0; }
  .idx-dpr-count {
    display: flex; align-items: center; gap: 6px;
    margin-top: 6px; font-size: 15px; font-weight: 700; color: #047857;
  }
  .idx-dpr-banner {
    display: flex; align-items: center; gap: 6px;
    background: #047857; color: #ffffff; font-weight: 800; line-height: 1.25;
  }
  .idx-dpr-banner-featured {
    position: absolute; left: 0; right: 0; bottom: 0;
    font-size: 14px; padding: 10px 14px;
  }
  /* Pop-out card with the flag: the card becomes a column so the banner sits
     at the bottom, and its negative side margins cancel the card's padding
     (132px left, under the photo, and 14px right) so it spans the full width.
     Bottom corners follow the card's 16px radius less its 1px border. */
  .idx-pop.has-dpr { display: flex; flex-direction: column; padding-bottom: 0; }
  .idx-pop.has-dpr .idx-pop-body { flex: 1; }
  .idx-dpr-banner-pop {
    margin: 12px -14px 0 -132px; padding: 8px 14px;
    font-size: 13px; border-radius: 0 0 15px 15px;
  }
  .idx-dpr-box {
    margin: 12px 0 6px; padding: 14px 16px;
    background: #ecfdf5; border: 1.5px solid #047857; border-radius: 14px;
  }
  .idx-dpr-box-title { display: flex; align-items: center; gap: 6px; font-size: 16px; font-weight: 800; color: #065f46; }
  .idx-dpr-box-note { margin-top: 4px; font-size: 13px; color: #065f46; }
  .idx-dpr-ask {
    display: block; width: 100%; min-height: 44px; margin-top: 10px; cursor: pointer;
    background: #047857; color: #ffffff; border: none; border-radius: 10px; font-size: 14px; font-weight: 700;
  }
  .idx-dpr-ask:focus-visible { outline: 3px solid #fdbf00; outline-offset: 2px; }

  .idx-thanks { margin-top: 18px; padding: 14px 16px; border-radius: 12px; background: #eef7ee; color: #1f6b33; font-weight: 700; font-size: 14px; }
`
